import React, { useContext, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
/// Image
import profile from "../../../images/avatar/profile-1.jpg";
import { Dropdown } from "react-bootstrap";
import LogoutPage from './Logout';
import { ThemeContext } from "../../../context/ThemeContext";
import { Fn_FillListData } from "../../../store/Functions";
import { API_WEB_URLS } from "../../../constants/constAPI";
import { checkRegistrationExpiry } from "../../utils/RegistrationExpiryHelper";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const notifyStorage = () => {
  window.dispatchEvent(new Event("storage"));
  window.dispatchEvent(new Event("sessionChange"));
};

const Header = ({ onNote }) => {
  const dispatch = useDispatch();
  const { background, changeBackground } = useContext(ThemeContext);
  const isDark = background?.value === "dark";

  // Session Selector States
  const [firmList, setFirmList] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedFirm") || "null"); } catch { return null; }
  });
  const [branchList, setBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem("selectedBranch") || "null"); } catch { return null; }
  });
  const [selectedMonth, setSelectedMonth] = useState(() => localStorage.getItem("selectedPayrollMonth") || "");
  const [selectedYear, setSelectedYear] = useState(() => localStorage.getItem("selectedPayrollYear") || "");

  // Sync state from localStorage
  const syncFromStorage = useCallback(() => {
    try {
      const sf = JSON.parse(localStorage.getItem("selectedFirm") || "null");
      setSelectedFirm(sf);
    } catch {
      setSelectedFirm(null);
    }
    try {
      const sb = JSON.parse(localStorage.getItem("selectedBranch") || "null");
      setSelectedBranch(sb);
    } catch {
      setSelectedBranch(null);
    }
    setSelectedMonth(localStorage.getItem("selectedPayrollMonth") || "");
    setSelectedYear(localStorage.getItem("selectedPayrollYear") || "");
  }, []);

  // Listen to storage events from anywhere
  useEffect(() => {
    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, [syncFromStorage]);

  // Load firm list on mount
  useEffect(() => {
    const apiURL = API_WEB_URLS.MASTER + "/0/token/" + API_WEB_URLS.FirmMaster + "/Id/0";
    Fn_FillListData(dispatch, setFirmList, "gridData", apiURL);
  }, [dispatch]);

  // Load branches when selectedFirm changes
  useEffect(() => {
    if (!selectedFirm || !selectedFirm.Id) {
      setBranchList([]);
      setSelectedBranch(null);
      return;
    }
    const branchApi = API_WEB_URLS.MASTER + "/0/token/GetBranchByFirmId/Id/" + selectedFirm.Id;
    Fn_FillListData(dispatch, setBranchList, "gridData", branchApi).then((dataList) => {
      if (dataList && dataList.length > 0) {
        const saved = localStorage.getItem("selectedBranch");
        let validSavedBranch = null;
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            validSavedBranch = dataList.find(b => String(b.Id) === String(parsed.Id));
          } catch {}
        }

        if (validSavedBranch) {
          setSelectedBranch(validSavedBranch);
        } else if (dataList.length === 1) {
          const singleBranch = dataList[0];
          setSelectedBranch(singleBranch);
          localStorage.setItem("selectedBranch", JSON.stringify(singleBranch));
          notifyStorage();
        } else {
          setSelectedBranch(null);
          localStorage.removeItem("selectedBranch");
          notifyStorage();
        }
      } else {
        setBranchList([]);
        setSelectedBranch(null);
        localStorage.removeItem("selectedBranch");
        notifyStorage();
      }
    }).catch(() => {
      setBranchList([]);
      setSelectedBranch(null);
      localStorage.removeItem("selectedBranch");
      notifyStorage();
    });
  }, [selectedFirm?.Id, dispatch]);

  const handleFirmChange = (e) => {
    const id = e.target.value;
    if (!id) {
      setSelectedFirm(null);
      setSelectedBranch(null);
      setBranchList([]);
      localStorage.removeItem("selectedFirm");
      localStorage.removeItem("selectedBranch");
      notifyStorage();
      return;
    }
    const firm = firmList.find((f) => String(f.Id) === String(id));
    if (firm) {
      setSelectedFirm(firm);
      localStorage.setItem("selectedFirm", JSON.stringify(firm));
      localStorage.removeItem("selectedBranch");
      notifyStorage();
    }
  };

  const handleBranchChange = (e) => {
    const bid = e.target.value;
    if (!bid) {
      setSelectedBranch(null);
      localStorage.removeItem("selectedBranch");
      notifyStorage();
      return;
    }
    const branch = branchList.find((b) => String(b.Id) === String(bid));
    if (branch) {
      setSelectedBranch(branch);
      localStorage.setItem("selectedBranch", JSON.stringify(branch));
      notifyStorage();
    }
  };

  const handleMonthChange = (e) => {
    const val = e.target.value;
    setSelectedMonth(val);
    if (val) {
      localStorage.setItem("selectedPayrollMonth", val);
    } else {
      localStorage.removeItem("selectedPayrollMonth");
    }
    notifyStorage();
  };

  const handleYearChange = (e) => {
    const val = e.target.value;
    setSelectedYear(val);
    if (val) {
      localStorage.setItem("selectedPayrollYear", val);
    } else {
      localStorage.removeItem("selectedPayrollYear");
    }
    notifyStorage();
  };

  // Check Registration Expiry for currently selected branch
  const expiryAlert = checkRegistrationExpiry(selectedBranch);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
  
  // Function to get role name based on F_UserType
  const getRoleName = (userType) => {
    switch (userType) {
      case 1:
        return "Admin";
      case 2:
        return "Supervisor";
      case 3:
        return "Operator";
      default:
        return "User";
    }
  };

  const toggleTheme = () => {
    const nextThemeValue = background.value === 'light' ? 'dark' : 'light';
    changeBackground({ value: nextThemeValue, label: nextThemeValue === 'dark' ? 'Dark' : 'Light' });
  };

  return (
    <div className="header">
      <div className="header-content">
        <nav className="navbar navbar-expand">
          <div className="collapse navbar-collapse justify-content-between">
            <div className="header-left d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
              {/* User Role and Name Display */}
              <div className="user-info d-none d-lg-block" style={{ padding: '6px 12px', background: isDark ? '#1e2638' : '#f8f9fa', borderRadius: '8px', border: `1px solid ${isDark ? '#2e3a52' : '#e9ecef'}` }}>
                <span className="font-w600" style={{ fontSize: '12.5px', color: isDark ? '#ced4da' : '#495057' }}>
                  <i className="fa fa-user-circle text-primary me-1" />
                  {getRoleName(userData?.userType)} : <strong>{userData?.name || 'User'}</strong>
                </span>
              </div>

              {/* Header Session Selectors */}
              <div className="d-flex align-items-center flex-wrap my-1" style={{ gap: '8px' }}>
                {/* Firm Selector */}
                <div 
                  className="d-flex align-items-center" 
                  style={{ 
                    background: isDark ? '#1e2638' : '#f1f5f9', 
                    border: `1px solid ${selectedFirm ? '#3b82f6' : (isDark ? '#334155' : '#cbd5e1')}`, 
                    borderRadius: '8px', 
                    padding: '3px 8px',
                    transition: 'all 0.2s'
                  }}
                  title="Select Active Firm"
                >
                  <i className="fa fa-building text-primary me-2" style={{ fontSize: '13px' }} />
                  <select
                    className="form-select form-select-sm border-0 bg-transparent"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: selectedFirm ? 600 : 400,
                      color: isDark ? '#f8fafc' : '#1e293b', 
                      cursor: 'pointer',
                      maxWidth: '170px',
                      minWidth: '130px',
                      padding: '2px 18px 2px 0',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                    value={selectedFirm ? selectedFirm.Id : ""}
                    onChange={handleFirmChange}
                  >
                    <option value="" style={{ color: '#000' }}>-- Select Firm --</option>
                    {firmList.map((firm) => (
                      <option key={firm.Id} value={firm.Id} style={{ color: '#000' }}>{firm.Name}</option>
                    ))}
                  </select>
                </div>

                {/* Branch Selector (if > 1 branch) */}
                {branchList.length > 1 && (
                  <div 
                    className="d-flex align-items-center" 
                    style={{ 
                      background: isDark ? '#1e2638' : '#f1f5f9', 
                      border: `1px solid ${selectedBranch ? '#06b6d4' : (isDark ? '#334155' : '#cbd5e1')}`, 
                      borderRadius: '8px', 
                      padding: '3px 8px',
                      transition: 'all 0.2s'
                    }}
                    title="Select Active Branch"
                  >
                    <i className="fa fa-code-fork text-info me-2" style={{ fontSize: '13px' }} />
                    <select
                      className="form-select form-select-sm border-0 bg-transparent"
                      style={{ 
                        fontSize: '12px', 
                        fontWeight: selectedBranch ? 600 : 400,
                        color: isDark ? '#f8fafc' : '#1e293b', 
                        cursor: 'pointer',
                        maxWidth: '130px',
                        minWidth: '100px',
                        padding: '2px 18px 2px 0',
                        outline: 'none',
                        boxShadow: 'none'
                      }}
                      value={selectedBranch ? selectedBranch.Id : ""}
                      onChange={handleBranchChange}
                    >
                      <option value="" style={{ color: '#000' }}>-- Branch --</option>
                      {branchList.map((branch) => (
                        <option key={branch.Id} value={branch.Id} style={{ color: '#000' }}>{branch.Name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Branch Badge (if single branch auto-selected) */}
                {branchList.length === 1 && selectedBranch && (
                  <span 
                    className="badge d-flex align-items-center px-2 py-1"
                    style={{ 
                      background: isDark ? 'rgba(6, 182, 212, 0.15)' : '#e0f2fe',
                      color: isDark ? '#38bdf8' : '#0284c7',
                      border: `1px solid ${isDark ? '#0284c7' : '#bae6fd'}`,
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600
                    }}
                    title={`Branch: ${selectedBranch.Name}`}
                  >
                    <i className="fa fa-code-fork me-1" />
                    {selectedBranch.Name}
                  </span>
                )}

                {/* Month Selector */}
                <div 
                  className="d-flex align-items-center" 
                  style={{ 
                    background: isDark ? '#1e2638' : '#f1f5f9', 
                    border: `1px solid ${selectedMonth ? '#f59e0b' : (isDark ? '#334155' : '#cbd5e1')}`, 
                    borderRadius: '8px', 
                    padding: '3px 8px',
                    transition: 'all 0.2s'
                  }}
                  title="Wage Month"
                >
                  <i className="fa fa-calendar text-warning me-2" style={{ fontSize: '13px' }} />
                  <select
                    className="form-select form-select-sm border-0 bg-transparent"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: selectedMonth ? 600 : 400,
                      color: isDark ? '#f8fafc' : '#1e293b', 
                      cursor: 'pointer',
                      maxWidth: '115px',
                      minWidth: '95px',
                      padding: '2px 18px 2px 0',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    <option value="" style={{ color: '#000' }}>-- Month --</option>
                    {MONTH_NAMES.map((m) => (
                      <option key={m} value={m} style={{ color: '#000' }}>{m}</option>
                    ))}
                  </select>
                </div>

                {/* Year Selector */}
                <div 
                  className="d-flex align-items-center" 
                  style={{ 
                    background: isDark ? '#1e2638' : '#f1f5f9', 
                    border: `1px solid ${selectedYear ? '#10b981' : (isDark ? '#334155' : '#cbd5e1')}`, 
                    borderRadius: '8px', 
                    padding: '3px 8px',
                    transition: 'all 0.2s'
                  }}
                  title="Wage Year"
                >
                  <i className="fa fa-clock-o text-success me-2" style={{ fontSize: '13px' }} />
                  <select
                    className="form-select form-select-sm border-0 bg-transparent"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: selectedYear ? 600 : 400,
                      color: isDark ? '#f8fafc' : '#1e293b', 
                      cursor: 'pointer',
                      maxWidth: '85px',
                      minWidth: '75px',
                      padding: '2px 18px 2px 0',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                    value={selectedYear}
                    onChange={handleYearChange}
                  >
                    <option value="" style={{ color: '#000' }}>-- Year --</option>
                    {YEAR_LIST.map((y) => (
                      <option key={y} value={y} style={{ color: '#000' }}>{y}</option>
                    ))}
                  </select>
                </div>

                {/* Registration Expiry Navbar Alert Pill */}
                {expiryAlert && (
                  <span 
                    className={`badge d-inline-flex align-items-center py-1 px-2 ${expiryAlert.isExpired ? 'bg-danger text-white' : 'bg-warning text-dark'}`}
                    style={{ 
                      borderRadius: '6px', 
                      fontSize: '11.5px', 
                      fontWeight: 600,
                      animation: expiryAlert.isExpired ? 'pulse-alert-red 1.8s infinite' : 'pulse-alert-amber 1.8s infinite',
                      cursor: 'pointer'
                    }}
                    title={`Branch: ${expiryAlert.branchName} | Reg No: ${expiryAlert.fbRegNo || 'N/A'} | Expiry: ${expiryAlert.formattedDate}`}
                  >
                    <i className={`fa ${expiryAlert.isExpired ? 'fa-exclamation-triangle' : 'fa-clock-o'} me-1`} />
                    {expiryAlert.isExpired ? (
                      <>Reg. Expired ({expiryAlert.formattedDate})</>
                    ) : (
                      <>Reg. Expiring: {expiryAlert.diffDays} Days ({expiryAlert.formattedDate})</>
                    )}
                  </span>
                )}

                {/* Active Session Status Badge (shown when no urgent expiry alert) */}
                {selectedFirm && selectedMonth && selectedYear && !expiryAlert && (
                  <span 
                    className="badge d-none d-md-inline-flex align-items-center py-1 px-2" 
                    style={{ 
                      background: isDark ? 'rgba(16, 185, 129, 0.15)' : '#dcfce7',
                      color: isDark ? '#34d399' : '#15803d',
                      border: `1px solid ${isDark ? '#059669' : '#86efac'}`,
                      borderRadius: '6px', 
                      fontSize: '11px', 
                      fontWeight: 600 
                    }}
                    title="Active Payroll Session Configured"
                  >
                    <span 
                      style={{ 
                        width: '6px', 
                        height: '6px', 
                        borderRadius: '50%', 
                        background: '#10b981',
                        display: 'inline-block',
                        marginRight: '5px'
                      }}
                    />
                    Session Ready
                  </span>
                )}
              </div>
            </div>
            
            <ul className="navbar-nav header-right main-notification">
              {/* Registration Expiry Notification Bell Dropdown */}
              <Dropdown as="li" className="nav-item dropdown notification_dropdown">
                <Dropdown.Toggle
                  variant=""
                  as="a"
                  className="nav-link ai-icon i-false c-pointer position-relative"
                  role="button"
                  title={expiryAlert ? `Warning: ${expiryAlert.branchName} Registration is ${expiryAlert.isExpired ? 'Expired' : 'Expiring Soon'}` : 'Notifications'}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: expiryAlert ? '#ef4444' : (isDark ? '#fff' : '#67748e') }}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  {expiryAlert && (
                    <span 
                      className="badge light text-white bg-danger rounded-circle position-absolute" 
                      style={{ top: '8px', right: '4px', width: '16px', height: '16px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}
                    >
                      1
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="mt-3 dropdown-menu dropdown-menu-end p-0 shadow-lg" style={{ minWidth: '320px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                  <div className="p-3 border-bottom d-flex align-items-center justify-content-between" style={{ background: expiryAlert ? (expiryAlert.isExpired ? '#fef2f2' : '#fffbeb') : (isDark ? '#1e2638' : '#f8f9fa'), borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <h6 className="mb-0 fw-bold fs-13" style={{ color: expiryAlert ? (expiryAlert.isExpired ? '#991b1b' : '#92400e') : (isDark ? '#fff' : '#333') }}>
                      <i className={`fa ${expiryAlert ? (expiryAlert.isExpired ? 'fa-exclamation-circle text-danger' : 'fa-bell text-warning') : 'fa-bell-o text-muted'} me-2`} />
                      {expiryAlert ? "Registration Expiry Alert" : "System Notifications"}
                    </h6>
                    {expiryAlert && (
                      <span className={`badge ${expiryAlert.isExpired ? 'bg-danger' : 'bg-warning text-dark'}`} style={{ fontSize: '10px' }}>
                        {expiryAlert.isExpired ? 'EXPIRED' : 'EXPIRING SOON'}
                      </span>
                    )}
                  </div>

                  <div className="p-3">
                    {expiryAlert ? (
                      <div>
                        <div className="d-flex align-items-start mb-2">
                          <div 
                            className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                            style={{ 
                              width: '36px', 
                              height: '36px', 
                              background: expiryAlert.isExpired ? '#fee2e2' : '#fef3c7',
                              color: expiryAlert.isExpired ? '#dc2626' : '#d97706',
                              fontSize: '16px' 
                            }}
                          >
                            <i className={`fa ${expiryAlert.isExpired ? 'fa-exclamation-triangle' : 'fa-clock-o'}`} />
                          </div>
                          <div>
                            <strong className="text-dark fs-13 d-block">{expiryAlert.branchName}</strong>
                            <span className="text-muted fs-12 d-block">
                              FB Reg No: <strong>{expiryAlert.fbRegNo || 'N/A'}</strong>
                            </span>
                          </div>
                        </div>

                        <div 
                          className="p-2 rounded mb-2"
                          style={{ 
                            background: expiryAlert.isExpired ? 'rgba(239, 68, 68, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                            border: `1px solid ${expiryAlert.isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`,
                            fontSize: '12px'
                          }}
                        >
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Expiry Date:</span>
                            <strong className={expiryAlert.isExpired ? 'text-danger' : 'text-dark'}>{expiryAlert.formattedDate}</strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Status:</span>
                            <strong className={expiryAlert.isExpired ? 'text-danger' : 'text-warning'}>
                              {expiryAlert.isExpired ? `Expired (${expiryAlert.diffDays} days ago)` : `Expiring in ${expiryAlert.diffDays} days`}
                            </strong>
                          </div>
                        </div>

                        <p className="text-muted mb-0 fs-11" style={{ lineHeight: '1.4' }}>
                          ⚠️ Please renew factory / establishment registration before the expiry date to avoid compliance penalties.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-3 text-muted fs-13">
                        <i className="fa fa-check-circle text-success fa-2x mb-2 d-block" />
                        No pending alerts. All active registrations are valid.
                      </div>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>

              {/* Profile Dropdown */}
              <Dropdown as="li" className="nav-item dropdown header-profile">
                <Dropdown.Toggle variant="" as="a" className="nav-link i-false c-pointer"
                  role="button" data-toggle="dropdown"
                >
                  <img src={profile} width={20} alt="" />
                </Dropdown.Toggle>

                <Dropdown.Menu align="right" className="mt-3 dropdown-menu dropdown-menu-end">
                  <LogoutPage />
                </Dropdown.Menu>
              </Dropdown> 

              {/* Keyboard Shortcuts Help Button */}
              <li className="nav-item">
                <button
                  onClick={() => window.dispatchEvent(new Event('toggle-keyboard-shortcuts'))}
                  className="nav-link ai-icon i-false c-pointer btn btn-link"
                  aria-label="Keyboard Shortcuts Help"
                  title="Keyboard Shortcuts Help (Alt + K)"
                  style={{ border: 'none', background: 'none', padding: '0.375rem 0.75rem', color: background?.value === 'light' ? '#67748e' : '#fff' }}
                >
                  <i className="fa fa-keyboard" style={{ fontSize: '22px' }}></i>
                </button>
              </li>

              {/* Theme Toggle Button */}
              <li className="nav-item">
                <button
                  onClick={toggleTheme}
                  className="nav-link ai-icon i-false c-pointer btn btn-link"
                  aria-label={background?.value === 'light' ? 'Activate dark mode' : 'Activate light mode'}
                  style={{ border: 'none', background: 'none', padding: '0.375rem 0.75rem' }}
                >
                  {background?.value === 'light' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-moon">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-sun">
                      <circle cx="12" cy="12" r="5"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                  )}
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      {/* Embedded CSS for pulsing alert animations */}
      <style>{`
        @keyframes pulse-alert-red {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
        @keyframes pulse-alert-amber {
          0% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
          }
          70% {
            box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default Header;
